import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gem, Gift, Sparkle, ArrowRight, ChevronDown } from "lucide-react";
import { FPM_TILES, useFpmOptions } from "../../hooks/useHomeContent";

const iconMap = { gem: Gem, gift: Gift, sparkle: Sparkle };

// Map FPM tile label → parent key for its options
const parentFor = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("metal")) return "metal";
  if (l.includes("occasion")) return "occasion";
  return "style";
};

function FPMTile({ p, index }) {
  const Icon = iconMap[p.icon] || Sparkle;
  const options = useFpmOptions(parentFor(p.label));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <motion.div
      className="fpm-tile"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      viewport={{ once: true, amount: 0.2 }}
      whileHover={{ y: -6 }}
      ref={ref}
    >
      <button
        type="button"
        className="fpm-tile-link"
        onClick={() => setOpen((s) => !s)}
        data-testid={`fpm-${parentFor(p.label)}`}
      >
        <span className="fpm-tile-icon">
          <Icon size={22} strokeWidth={1.4} />
        </span>
        <strong>{p.label}</strong>
        <span className="fpm-tile-sub">{p.sub}</span>
        <span className="fpm-tile-arrow">
          <ChevronDown
            size={14}
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fpm-menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            {options.map((o) => (
              <Link
                key={o.id || o.label}
                to={o.link || "/"}
                className="fpm-menu-item"
                onClick={() => setOpen(false)}
                data-testid={`fpm-option-${(o.label || "").toLowerCase().replace(/\s+/g, "-")}`}
              >
                {o.label}
                <ArrowRight size={12} />
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FindPerfectMatch() {
  return (
    <section className="fpm">
      <div className="wrap">
        <motion.div
          className="fpm-head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="fpm-eyebrow">Guided Discovery</span>
          <h2>Find your perfect match</h2>
          <p>
            Tell us how you shop — by metal, occasion or style — and we&apos;ll
            surface the pieces you&apos;ll love.
          </p>
        </motion.div>

        <div className="fpm-grid fpm-grid-3">
          {FPM_TILES.map((p, i) => (
            <FPMTile key={p.label} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
